import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { howItWorksSteps, services } from '../mock';
import PageHero from '../components/shared/PageHero';
import CtaBlock from '../components/shared/CtaBlock';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Wallet, ShieldCheck, TrendingUp, Check, ArrowRight, MessageSquare, ClipboardList, Play, RefreshCw } from 'lucide-react';

const iconMap = { Wallet, ShieldCheck, TrendingUp };
const stepIcons = [MessageSquare, ClipboardList, Play, RefreshCw];

const loanUses = [
  { title: 'Debt Consolidation', desc: 'Combine multiple high-interest debts into a single, more manageable monthly payment.' },
  { title: 'Home Improvements', desc: "Fund renovations, repairs, or upgrades to increase your home's value and comfort." },
  { title: 'Major Purchases', desc: 'Finance important purchases like appliances, furniture, or electronics with predictable payments.' },
  { title: 'Emergency Expenses', desc: 'Cover unexpected costs like medical bills, car repairs, or other urgent financial needs.' },
  { title: 'Business Expenses', desc: 'Fund small business needs, startup costs, or business expansion projects.' },
  { title: 'Travel & Events', desc: 'Finance vacations, weddings, or other special events with structured repayment plans.' }
];

const protectionFeatures = [
  { title: 'Triple-Bureau Monitoring', desc: 'Continuous monitoring of your Experian, Equifax, and TransUnion credit reports for changes.' },
  { title: 'Instant Alerts', desc: 'Real-time notifications about new accounts, inquiries, and other important credit changes.' },
  { title: 'Dark Web Monitoring', desc: 'Scans the dark web for your personal information, including SSN, email, and financial accounts.' },
  { title: 'Credit Reports & Scores', desc: 'Regular access to your credit reports and scores from all three major bureaus.' },
  { title: 'Identity Theft Insurance', desc: 'Up to $1 million in coverage for eligible expenses related to identity theft recovery.' },
  { title: 'Dedicated Support', desc: 'Access to fraud resolution specialists who can help you address identity theft issues.' }
];

const buildingSolutions = [
  { title: 'Credit Builder Loans', desc: 'Specialized loans designed specifically to help establish or improve your credit history.' },
  { title: 'Secured Credit Cards', desc: 'Cards backed by a security deposit that help you build credit with responsible use.' },
  { title: 'Credit Report Analysis', desc: 'Thorough review of your credit reports to identify areas for improvement.' },
  { title: 'Credit Coaching', desc: 'One-on-one guidance from credit specialists to help you make smart financial decisions.' },
  { title: 'Dispute Resolution', desc: 'Assistance with identifying and disputing inaccurate items on your credit report.' },
  { title: 'Financial Education', desc: 'Resources and guidance to help you understand credit and develop healthy financial habits.' }
];

export default function HowItWorks() {
  return (
    <>
      <PageHero
        eyebrow="Comprehensive Guide"
        title={<>How <span className="text-emerald-600">The Loan Alliance</span> Works</>}
        subtitle="Your comprehensive guide to our financial services and solutions."
        ctaLabel="Explore Our Services"
        ctaTo="/#services"
      />

      {/* Three services */}
      <section id="services" className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-slate-900 font-bold">Our Three Core Services</h2>
            <p className="mt-4 text-slate-600">Comprehensive financial solutions tailored to your needs.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {services.map((s) => {
              const Icon = iconMap[s.icon];
              return (
                <div key={s.id} className="group rounded-2xl border border-slate-200 bg-white p-7 hover:border-emerald-300 hover:shadow-lg transition-all">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="mt-5 font-serif-display text-2xl font-bold text-slate-900">{s.title}</h3>
                  <p className="mt-3 text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                  <Link to={s.to} className="mt-5 inline-flex items-center gap-1.5 text-emerald-700 font-semibold text-sm hover:text-emerald-800">
                    Learn More <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                </div>
              );
            })}
          </div>

          <div className="mt-16 rounded-3xl bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-100 px-8 py-10 lg:px-14 lg:py-14 grid lg:grid-cols-2 gap-6 items-center">
            <div>
              <h3 className="font-serif-display text-2xl lg:text-3xl text-slate-900 font-bold">Our Commitment to You</h3>
            </div>
            <p className="text-slate-700 leading-relaxed">
              At The Loan Alliance, we're committed to providing transparent, unbiased comparisons that help you achieve your financial goals. Whether you need a loan, want to protect your credit, or are building a stronger financial future, our editorial team is here to help you compare with confidence every step of the way.
            </p>
          </div>
        </div>
      </section>

      {/* 4-step process */}
      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-slate-900 font-bold">Our Simple 4-Step Process</h2>
            <p className="mt-4 text-slate-600">How we work with you across all our services.</p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-gradient-to-r from-emerald-200 via-emerald-400 to-emerald-200" />
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {howItWorksSteps.map((s, i) => {
                const Icon = stepIcons[i];
                return (
                  <div key={s.step} className="relative rounded-2xl bg-white border border-slate-200 p-6 hover:border-emerald-300 hover:shadow-lg transition-all">
                    <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-2xl mx-auto shadow-lg shadow-emerald-500/30">
                      {s.step}
                    </div>
                    <div className="mt-4 flex justify-center">
                      <div className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-emerald-700" />
                      </div>
                    </div>
                    <h4 className="mt-4 text-lg font-bold text-slate-900 text-center">{s.title}</h4>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed text-center">{s.desc}</p>
                    <ul className="mt-4 space-y-1.5 pt-4 border-t border-slate-100">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2 text-xs text-slate-600"><Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0" /> {b}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Detailed tabs */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl text-slate-900 font-bold">Our Services in Detail</h2>
            <p className="mt-4 text-slate-600">Explore how each of our services works.</p>
          </div>
          <Tabs defaultValue="loans" className="w-full">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3 h-11 bg-slate-100 mb-8">
              <TabsTrigger value="loans">Personal Loans</TabsTrigger>
              <TabsTrigger value="protection">Credit Protection</TabsTrigger>
              <TabsTrigger value="building">Credit Building</TabsTrigger>
            </TabsList>

            <TabsContent value="loans">
              <ServiceDetail
                title="Personal Loans"
                desc="Our personal loan services connect you with lenders offering competitive rates and flexible terms."
                benefits={['Access to multiple lenders with one application','Loans from $1,000 to $50,000','Competitive interest rates based on your credit profile','Fast funding, often within 1-3 business days','Fixed monthly payments for easy budgeting','No prepayment penalties']}
                stepsTitle="How Our Personal Loan Process Works"
                steps={[
                  { t: 'Complete Our Simple Application', d: 'Fill out our secure online form with your basic information and loan requirements.' },
                  { t: 'Review Multiple Loan Offers', d: 'We match you with lenders in our network who can meet your needs.' },
                  { t: 'Select Your Preferred Offer', d: 'Choose the loan that best fits your needs and complete the lender’s application process.' },
                  { t: 'Receive Your Funds', d: 'After approval, funds are typically deposited directly into your bank account within 1-3 business days.' }
                ]}
                cta={{ label: 'Check Your Rate', to: '/loan', note: 'Checking your rate won’t affect your credit score' }}
                extraTitle="Common Uses for Personal Loans"
                extras={loanUses}
              />
            </TabsContent>
            <TabsContent value="protection">
              <ServiceDetail
                title="Credit Protection"
                desc="Our credit protection services help you safeguard your credit score and identity from fraud, errors, and unexpected changes."
                benefits={['24/7 monitoring of your credit reports from all three bureaus','Real-time alerts for suspicious activity','Identity theft insurance up to $1 million','Dark web monitoring for personal information','Credit score tracking and analysis','Dedicated fraud resolution specialists']}
                stepsTitle="How Our Credit Protection Works"
                steps={[
                  { t: 'Enroll in Protection Services', d: 'Sign up for our credit protection plan and provide the necessary information.' },
                  { t: 'Continuous Monitoring Begins', d: 'Our systems start monitoring your credit reports, personal information, and financial accounts.' },
                  { t: 'Receive Alerts & Updates', d: 'Get instant notifications about important changes to your credit report or potential fraud.' },
                  { t: 'Resolution Support If Needed', d: 'If issues arise, our fraud resolution specialists help you take appropriate action.' }
                ]}
                cta={{ label: 'Protect Your Credit Now', to: '/protect-credit', note: 'Plans start at just $9.95 per month' }}
                extraTitle="Our Credit Protection Services Include"
                extras={protectionFeatures}
              />
            </TabsContent>
            <TabsContent value="building">
              <ServiceDetail
                title="Credit Building"
                desc="Our credit building services help you establish or improve your credit score through proven strategies and specialized financial products."
                benefits={['Personalized credit building strategy','Access to specialized credit builder products','Regular credit score updates and analysis','Expert guidance and coaching','Dispute assistance for inaccurate items','Educational resources to improve financial literacy']}
                stepsTitle="How Our Credit Building Process Works"
                steps={[
                  { t: 'Free Credit Consultation', d: 'We analyze your current credit situation and identify areas for improvement.' },
                  { t: 'Personalized Strategy Development', d: 'We create a customized credit building plan tailored to your specific situation.' },
                  { t: 'Account Setup & Implementation', d: 'We help you set up the appropriate credit building accounts and tools.' },
                  { t: 'Ongoing Monitoring & Coaching', d: 'We provide continuous monitoring of your progress and regular coaching.' }
                ]}
                cta={{ label: 'Start Building Credit', to: '/build-credit', note: 'Free consultation, no obligation' }}
                extraTitle="Our Credit Building Solutions"
                extras={buildingSolutions}
              />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <CtaBlock title="Ready to Compare Your Options?" subtitle="Start with The Loan Alliance today and pick the right financial product with confidence." ctaLabel="Start Comparing" />
    </>
  );
}

function ServiceDetail({ title, desc, benefits, stepsTitle, steps, cta, extraTitle, extras }) {
  return (
    <div className="space-y-10">
      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-7 lg:p-10">
        <h3 className="font-serif-display text-2xl lg:text-3xl text-slate-900 font-bold">{title}</h3>
        <p className="mt-3 text-slate-600 leading-relaxed">{desc}</p>
        <div className="mt-6">
          <h4 className="font-semibold text-slate-900 mb-3">Key Benefits:</h4>
          <div className="grid md:grid-cols-2 gap-2">
            {benefits.map((b) => (
              <div key={b} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {b}</div>
            ))}
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-serif-display text-2xl font-bold text-slate-900 mb-6">{stepsTitle}</h4>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="rounded-xl bg-white border border-slate-200 p-5 hover:border-emerald-300 hover:shadow-md transition-all">
              <div className="w-9 h-9 rounded-full bg-emerald-600 text-white text-sm font-bold flex items-center justify-center">{i + 1}</div>
              <h5 className="mt-3 font-semibold text-slate-900">{s.t}</h5>
              <p className="mt-1 text-xs text-slate-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 text-center">
          <Link to={cta.to}>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-6">{cta.label}</Button>
          </Link>
          <p className="mt-2 text-xs text-slate-500">{cta.note}</p>
        </div>
      </div>

      <div>
        <h4 className="font-serif-display text-2xl font-bold text-slate-900 mb-6">{extraTitle}</h4>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {extras.map((e) => (
            <div key={e.title} className="rounded-xl bg-white border border-slate-200 p-5 hover:border-emerald-300 transition-colors">
              <h5 className="font-semibold text-slate-900">{e.title}</h5>
              <p className="mt-1 text-sm text-slate-600 leading-relaxed">{e.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
